import type { GatewayConfig, MetricsPublisherConfig } from '../../../types/gateway-ent';
import type { GatewayServer } from '../../../gateway-server';
import type { OriginFilters, ServerWebSocketOptions } from '../../../types/web/server';
import type { AuthorizationRule } from '../../../types/auth';
import { IOGateway } from '@interopio/gateway';

function toMetricsFilters(legacy?: MetricsPublisherConfig['filters']): IOGateway.MetricFilters | undefined {
    if (legacy) {
        const publishers = legacy.publishers.map(publisher => {
            return { identity: publisher.publisher, metrics: publisher.metrics };
        });
        const non_matched = legacy['non-matched'];
        return { publishers, non_matched };
    }
}

function toMetricsPublisherConfig(legacy: MetricsPublisherConfig & {
    conflation?: { 'max-datapoints-repo'?: number } & MetricsPublisherConfig['conflation']
}): IOGateway.BasicMetricsPublisherConfig {
    const filters = toMetricsFilters(legacy?.filters);
    const conflation = toConflation(legacy?.conflation);
    const result: IOGateway.BasicMetricsPublisherConfig = { ...legacy, filters, conflation } as const;
    if (!filters) {
        delete result.filters;
    }
    if (!conflation) {
        delete result.conflation;
    }
    // if (legacy.heartbeats !== undefined) {
    //     result.heartbeats = legacy.heartbeats;
    // }
    // if (legacy.buffer_size !== undefined) {
    //     result.buffer_size = legacy.buffer_size;
    // }
    // if (legacy['worker'] !== undefined) {
    //     result.worker = legacy.worker;
    // }
    return result;
}

function toConflation(legacy?: {
    'max-datapoints-repo'?: number
} & MetricsPublisherConfig['conflation']): IOGateway.BasicMetricsPublisherConfig['conflation'] | undefined {
    if (legacy) {
        return { interval: legacy.interval };
    }
}

function toMetricsConfig(legacy: GatewayConfig['metrics']): IOGateway.GatewayConfig['metrics'] {
    const metrics: IOGateway.GatewayConfig['metrics'] = { publishers: [] };
    legacy?.publishers?.forEach((publisher) => {
        if (typeof publisher === 'string') {
            if (publisher === 'rest') {
                metrics.publishers.push('rest');
                if (legacy.rest) {
                    const conf = { ...legacy.rest };
                    const userAgent = conf["user-agent"];
                    delete conf['user-agent'];
                    const headers = { ...conf.headers, ...(userAgent ? { 'user-agent': userAgent } : {}) };
                    delete conf.headers;
                    metrics.rest = {
                        endpoint: conf.endpoint,
                        headers: headers,
                        ...toMetricsPublisherConfig(conf)
                    };
                    metrics.rest['publishFn'] ??= '@interopio/gateway-server/metrics/publisher/rest';
                }
            }
            else if (publisher === 'file') {
                metrics.publishers.push('file');
                if (legacy.file) {
                    const conf = { ...legacy.file };
                    const status = conf['skip-status'] === undefined ? true : !conf['skip-status'];
                    delete conf['skip-status'];
                    metrics.file = {
                        location: conf.location,
                        status: status,
                        ...toMetricsPublisherConfig(conf)
                    };
                    metrics.file['publishFn'] ??= '@interopio/gateway/metrics/publisher/file';
                }
            }
            else {
                // unsupported predefined type
            }
        }
        else {
            const configuration = { ...publisher };
            const splitSize = configuration["split-size"];
            delete configuration["split-size"];
            const file = publisher['publisher']?.['file'] as string;
            const custom: IOGateway.CustomMetricsPublisherConfig = {
                split_size: splitSize,
                publisher: { file, configuration: publisher['publisher']?.['configuration'] },
                context: publisher.context as IOGateway.CustomMetricsPublisherConfig['context'],
                ...toMetricsPublisherConfig(configuration as MetricsPublisherConfig),
            };
            if (custom.split_size === undefined) {
                delete custom.split_size;
            }
            metrics.publishers.push(custom);
        }
    });
    if (legacy?.filters) {
        metrics.filters = toMetricsFilters(legacy?.filters);
    }
    return metrics;
}

type GatewayCluster = Required<GatewayConfig>['cluster'];
type P2P = Required<GatewayCluster>['p2p'];

function trimTrailingSlash(url?: string): string | undefined {
    return url?.endsWith('/') ? url.slice(0, -1) : url;
}

function toCluster(legacy?: P2P): IOGateway.MeshClusterConfig | undefined {
    if (legacy?.directory) {
        const legacyDirectory = legacy.directory;
        const config: IOGateway.MeshClusterConfig = { endpoint: legacy?.['endpoint'] };
        if (legacyDirectory.type === 'rest') {
            let directory: IOGateway.RestMeshDirectoryConfig | undefined = undefined;
            if (config.endpoint === undefined) {
                config.endpoint = trimTrailingSlash(legacyDirectory.config?.directory_uri)!;
            }
            else {
                directory = { uri: trimTrailingSlash(legacyDirectory.config?.directory_uri) };
            }
            if (legacyDirectory.config?.announce_interval) {
                directory ??= {};
                directory.interval = Number(legacyDirectory.config.announce_interval);
            }
            if (directory !== undefined) {
                config.directory = directory;
                config.directory.metadata ??= legacyDirectory.config?.['metadata'] as Record<string, string>;
            }
            if (legacyDirectory.config) {
                const opts = {...legacyDirectory.config} as Exclude<IOGateway.MeshClusterConfig['opts'], undefined>;
                delete opts['directory_uri'];
                delete opts['announce_interval'];
                if (Object.keys(opts).length > 0) {
                    config.opts = opts;
                }
            }
            return config;
        }
        else if (legacyDirectory.type === 'static') {
            config.directory = { members: legacyDirectory.members ?? [] };
            return config;
        }
    }
}

function toMeshConfig(legacy: GatewayConfig['cluster']) {
    const mesh: IOGateway.MeshConfig = { auth: { user: null } };
    if (legacy?.configuration?.node_id) {
        mesh.node = legacy.configuration.node_id;
    }
    if (legacy?.configuration?.['user']) {
        mesh.auth.user = legacy.configuration?.['user'] as string | string[];
    }
    if (legacy?.type === 'broker') {
        mesh.broker = { endpoint: legacy.broker?.endpoint ?? '<unresolved>' };
    }
    if (legacy?.type === 'p2p') {
        const cluster = toCluster(legacy.p2p);
        if (cluster) {
            mesh.cluster = cluster;
        }
    }
    return mesh;
}

function toAuthenticationConfig(legacy: GatewayConfig['authentication']): IOGateway.AuthenticationConfig {
    const authentication: IOGateway.AuthenticationConfig & { available: string[] } = { available: [] };
    if (legacy?.default) {
        authentication.default = legacy.default as string;
    }

    const as = legacy?.available as string[];
    as.forEach((a) => {
        if (a === 'basic' || a === 'oauth2' || legacy?.[a]?.['authenticator'] !== undefined) {
            authentication.available.push(a);
            if (legacy?.[a] !== undefined) {
                authentication[a] = legacy[a];
            }
        }
    });

    return authentication;
}

export function toServerConfig(config: GatewayConfig): GatewayServer.ServerConfig {
    const gateway: GatewayServer.ServerConfig["gateway"] = {
        route: config.route,
        maxConnections: config.limits?.max_connections,
        origins: config.security?.origin_filters as OriginFilters,
        authorize: config['authorize'] as AuthorizationRule ?? { access: 'permitted' },
        ping: config['ping'] as ServerWebSocketOptions['ping'],
        clients: config['clients'] ?? { inactive_seconds: 0, buffer_size: 100 },
        contexts: {
            lifetime: 'retained',
            visibility: [
                { context: /___channel___.+/, restrictions: 'cluster' },
                { context: /T42\..+/, restrictions: 'local' }
            ]
        },
        methods: {
            visibility: [
                { method: /T42\..+/, restrictions: 'local' }
            ]
        },
        peers: {
            visibility: [
                { domain: 'context', restrictions: 'cluster' },
                { domain: 'agm', restrictions: 'local' }
            ]
        },
        metrics: { publishers: [] }
    };
    if (config.authentication !== undefined) {
        const tokenTtl = config.authentication.token_ttl ?? config.authentication['token-ttl'];
        if (tokenTtl !== undefined) {
            gateway.token = { ttl: tokenTtl as number };
        }
        if (config.authentication.available || config.authentication.default) {
            gateway.authentication = toAuthenticationConfig(config.authentication);
        }
    }
    if (config['globals']) {
        gateway.globals = config['globals'];
    }
    if (config['contexts']) {
        gateway.contexts = config['contexts'];
    }
    if (config['methods']) {
        gateway.methods = config['methods'];
    }
    if (config['peers']) {
        gateway.peers = config['peers'];
    }
    if (config.cluster?.enabled) {
        gateway.mesh = toMeshConfig(config.cluster);
    }
    if (config.metrics?.publishers) {
        gateway.metrics = toMetricsConfig(config.metrics);
    }
    return {
        port: config.port ?? 3434,
        host: config.ip ?? config['host'] as string,
        cors: config['cors'] === null ? undefined : config['cors'] ?? false,
        memory: config['memory'],
        app: async (configurer) => {
            if (config.cluster?.embedded_broker?.enabled === true) {
                configurer.socket({
                    path: config.cluster.embedded_broker.route ?? '/mesh-broker',
                    options: {
                        authorize: config.cluster.embedded_broker['authorize'] as AuthorizationRule ?? { access: 'permitted' }
                    },
                    factory: async (env: { endpoint: string }) => {
                        if (gateway.mesh?.broker?.endpoint === '<unresolved>') {
                            gateway.mesh.broker.endpoint = env.endpoint;
                        }
                        const delegate = (await import('../../mesh/ws/broker/core.ts')).default;
                        return await delegate(env);
                    }
                });
            }
        },
        gateway: gateway

    };
}
